/** Reset-password — consume the token from ?token= (POST /auth/reset-password). Public. */
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { apiResetPassword } from "@/auth/api";
import { AuthShell, authButtonClass, authErrorClass, authInputClass } from "@/auth/AuthShell";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await apiResetPassword(token, password);
      navigate("/login", { replace: true });
    } catch {
      setError(t("auth.resetInvalid"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell>
      <h1 className="text-base font-semibold">{t("auth.resetTitle")}</h1>
      {!token ? (
        <p className="text-sm text-fg-muted">{t("auth.resetInvalid")}</p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block text-sm">
            <span className="mb-1 block text-fg-muted">{t("auth.newPassword")}</span>
            <input
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={authInputClass}
            />
          </label>
          {error && (
            <p
              role="alert"
              className={authErrorClass}
              style={{ color: "rgb(var(--color-status-critical))" }}
            >
              {error}
            </p>
          )}
          <button type="submit" disabled={busy} className={authButtonClass}>
            {busy ? "…" : t("auth.resetSubmit")}
          </button>
        </form>
      )}
      <Link to="/login" className="block text-center text-sm text-brand hover:underline">
        {t("auth.backToSignIn")}
      </Link>
    </AuthShell>
  );
}
